/**
 * CNPJ — validation and normalisation.
 *
 * A CNPJ is a STRING, never a number (Master Specification section 11). Parsing
 * one as a number loses leading zeros and silently corrupts identity for every
 * company whose registration begins with 0.
 *
 * Two formats are valid simultaneously and will be for years:
 *
 *   - legacy numeric:  12.345.678/0001-95
 *   - alphanumeric:    12.ABC.345/01DE-35   (issued from July 2026)
 *
 * The alphanumeric format keeps the same 14 positions and the same modulo-11
 * check-digit algorithm. The only change is that each of the first twelve
 * characters is converted to a number by taking its ASCII code and subtracting
 * 48 — so '0'–'9' map to 0–9 exactly as before, and 'A'–'Z' map to 17–42.
 *
 * That is why one implementation covers both formats: the legacy numeric CNPJ
 * is not a separate case, it is this algorithm applied to characters that
 * happen to all be digits.
 *
 * Source: Receita Federal, "CNPJ Alfanumérico — Perguntas & Respostas",
 * questions 2 and 14, including the worked example reproduced in the tests.
 *
 * ---
 *
 * A limitation worth knowing, because it is easy to assume otherwise:
 *
 * The alphanumeric check digits do **not** catch every single-character typo.
 * A substitution is invisible to modulo 11 whenever the two characters' values
 * differ by a multiple of 11 — and since letters map to 17–42, differences of
 * 11, 22 and 33 are reachable. `D` (20) and `Z` (42) differ by 22, so swapping
 * one for the other leaves both check digits unchanged.
 *
 * The legacy numeric format never had this hole: digits map to 0–9, so no two
 * of them can differ by 11 or more.
 *
 * This is a property of the official algorithm, not of this implementation, and
 * it is why a CNPJ typed by a human should be confirmed against a register
 * rather than trusted because it validates. Treat `isValidCnpj` as "well
 * formed", never as "this company exists".
 */
const CNPJ_LENGTH = 14;
const BODY_LENGTH = 12;
/** ASCII offset from the Receita Federal conversion table: '0' is 48. */
const ASCII_ZERO = 48;
/**
 * Modulo-11 weights, identical to the legacy numeric algorithm.
 *
 * They run 2..9 from right to left and restart, which is why they read as
 * 5,4,3,2,9,8,7,6,… from the left.
 */
const FIRST_DIGIT_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const SECOND_DIGIT_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
/**
 * Shape of a normalised CNPJ: twelve body characters then two check digits.
 *
 * Tested against the whole string rather than character by character. Spreading
 * a string yields Unicode code points, which is the wrong unit here and is
 * flagged by lint for good reason — a single regex says what is allowed more
 * clearly anyway.
 */
const CNPJ_SHAPE = /^[0-9A-Z]{12}[0-9]{2}$/;
/** Fourteen of the same character. */
const ALL_IDENTICAL = /^(.)\1{13}$/;
/**
 * Strips formatting and upper-cases.
 *
 * Accepts what a human types or pastes — dots, slash, hyphen, spaces — and
 * nothing else. A character that is neither formatting nor a valid body
 * character is left in place so validation rejects it, rather than being
 * silently discarded into a value that then passes.
 */
export const normalizeCnpj = (input) => input.replace(/[.\-/\s]/g, '').toUpperCase();
const valueOfCharacter = (text, index) => text.charCodeAt(index) - ASCII_ZERO;
const checkDigitFor = (values, weights) => {
    let sum = 0;
    for (let index = 0; index < weights.length; index += 1) {
        sum += (values[index] ?? 0) * (weights[index] ?? 0);
    }
    const remainder = sum % 11;
    // The classic modulo-11 rule: a remainder of 0 or 1 would give a check digit
    // of 11 or 10, neither of which is a digit, so both collapse to 0.
    return remainder < 2 ? 0 : 11 - remainder;
};
/**
 * Computes both check digits for the twelve-character body.
 *
 * Exported because the seed needs to build a syntactically valid CNPJ, and
 * because a function that can *produce* the digits is the honest way to test
 * one that verifies them.
 */
export const cnpjCheckDigits = (body) => {
    const normalized = normalizeCnpj(body);
    if (normalized.length !== BODY_LENGTH) {
        throw new Error(`CNPJ body must have ${String(BODY_LENGTH)} characters, received ${String(normalized.length)}`);
    }
    const values = [];
    for (let index = 0; index < normalized.length; index += 1) {
        values.push(valueOfCharacter(normalized, index));
    }
    const first = checkDigitFor(values, FIRST_DIGIT_WEIGHTS);
    const second = checkDigitFor([...values, first], SECOND_DIGIT_WEIGHTS);
    return `${String(first)}${String(second)}`;
};
export const isValidCnpj = (input) => {
    const normalized = normalizeCnpj(input);
    if (normalized.length !== CNPJ_LENGTH)
        return false;
    if (!CNPJ_SHAPE.test(normalized))
        return false;
    /**
     * A CNPJ of fourteen identical characters is rejected.
     *
     * `00000000000000` genuinely satisfies the check digits — the sum is zero,
     * so both digits are zero — and it is the value an empty form produces when
     * someone pads a field. Rejecting repeated characters is a long-standing
     * convention rather than a rule from the Receita Federal document, and it is
     * recorded here as a deliberate addition.
     */
    if (ALL_IDENTICAL.test(normalized))
        return false;
    return (cnpjCheckDigits(normalized.slice(0, BODY_LENGTH)) ===
        normalized.slice(BODY_LENGTH));
};
/**
 * Formats for display: `12.ABC.345/01DE-35`.
 *
 * Returns the input untouched when it is not a valid CNPJ, because a formatter
 * that invents structure for invalid data makes the invalid data look correct.
 */
export const formatCnpj = (input) => {
    const normalized = normalizeCnpj(input);
    if (!isValidCnpj(normalized))
        return input;
    return [
        normalized.slice(0, 2),
        '.',
        normalized.slice(2, 5),
        '.',
        normalized.slice(5, 8),
        '/',
        normalized.slice(8, 12),
        '-',
        normalized.slice(12),
    ].join('');
};
/** True when every character is a digit — the pre-2026 format. */
export const isLegacyNumericCnpj = (input) => {
    const normalized = normalizeCnpj(input);
    return isValidCnpj(normalized) && /^[0-9]+$/.test(normalized);
};
//# sourceMappingURL=cnpj.js.map